import { Task, Submission, SupportTicket, Organization, CreateOrganizationData, Template, CreateTemplateData, PaginatedTasksResponse, OrganizationMembersResponse } from '../types';
import { useMemo } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const API_PREFIX = '/api/v1';

// Create a custom hook for API authentication
export const useApi = () => {
  // Use the local getAccessToken helper below
  
  // Create authenticated fetch wrapper
  const apiFetch = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
    try {
      const token = await getAccessToken();
      
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };
  
      const response = await fetch(`${API_BASE_URL}${API_PREFIX}${endpoint}`, {
        ...options,
        headers,
      });
  
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `API error: ${response.status}`);
      }
  
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  const createTemplate = async (data: CreateTemplateData): Promise<Template> => {
    return apiFetch('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  };

  const getTemplates = async (): Promise<PaginatedTasksResponse> => {
    return apiFetch('/interviewer/tasks');
  };

  const getTemplate = async (id: string): Promise<Template> => {
    return apiFetch(`/templates/${id}`);
  };

  const updateTemplate = async (id: string, data: Partial<CreateTemplateData>): Promise<Template> => {
    return apiFetch(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  };

  const deleteTemplate = async (id: string): Promise<void> => {
    return apiFetch(`/templates/${id}`, {
      method: 'DELETE',
    });
  };

  // Organization API endpoints
  const createOrganization = async (data: CreateOrganizationData): Promise<Organization> => {
    return apiFetch('/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  };

  const getOrganization = async (id: string): Promise<Organization> => {
    return apiFetch(`/organizations/${id}`);
  };

  const updateOrganization = async (id: string, data: Partial<CreateOrganizationData>): Promise<Organization> => {
    return apiFetch(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  };

  const getOrganizationMembers = async (orgId: string): Promise<OrganizationMembersResponse> => {
    return apiFetch(`/organizations/${orgId}/members`);
  };

  const inviteOrganizationMember = async (orgId: string, email: string, role: string): Promise<any> => { // TODO: Define specific response type
    return apiFetch(`/organizations/${orgId}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  };

  const resendInvitation = async (orgId: string, invitationId: string): Promise<any> => {
    return apiFetch(`/organizations/${orgId}/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  };

  // New function for org admin to get all tasks for their organization
  const getOrganizationTasks = async (): Promise<PaginatedTasksResponse> => {
    return apiFetch('/interviewer/tasks'); // Note: Uses interviewer endpoint
  };

  return useMemo(() => ({
    // User profile
    getUserProfile: (): Promise<any> => apiFetch('/users/me'), // TODO: Define UserProfile type
    updateUserProfile: (data: { 
      name?: string;
      bio?: string;
      location?: string;
      portfolio?: string;
      linkedin?: string;
      github?: string;
      phone?: string;
      skills?: string;
      timezone?: string;
    }): Promise<any> => apiFetch('/users/me', { // TODO: Define UserProfile type for return
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    
    // Organizations
    getUserOrganizations: (): Promise<Organization[]> => apiFetch('/users/me/organizations'),
    
    // Role-specific endpoints
    getCandidateDashboard: (): Promise<any> => apiFetch('/candidate/dashboard'), // TODO: Define CandidateDashboardData type
    getInterviewerDashboard: (): Promise<any> => apiFetch('/interviewer/candidates'), // TODO: Define InterviewerDashboardData type
    
    // Subscription (not explicitly defined in the API spec)
    // TODO: Clarify backend contract for subscription endpoints. Endpoint /organizations is generic.
    getSubscription: (): Promise<any> => apiFetch('/subscriptions/current'),
    getSubscriptionPlans: (): Promise<any> => apiFetch('/subscriptions/plans'),
    createCheckoutSession: (data: { orgId: string; planId: string; billingPeriod: 'monthly' | 'yearly' }): Promise<any> => 
      apiFetch('/subscriptions/checkout-session', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    verifyCheckoutSession: (sessionId: string): Promise<any> =>
      apiFetch('/subscriptions/verify-session', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      }),
    updateSubscription: (data: any): Promise<any> => apiFetch('/organizations', { // TODO: Define specific request and response types
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    
    // Tasks
    getTasks: (): Promise<PaginatedTasksResponse> => apiFetch('/interviewer/tasks'),
    getTask: (id: string): Promise<Task> => apiFetch(`/candidate/tasks/${id}`),
    createTask: (data: { templateId: string; candidateEmail: string }): Promise<Task | { error: string }> => 
      apiFetch('/interviewer/tasks/assign', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    inviteCandidate: (data: { email: string; role: string; taskTemplateId?: string }): Promise<any> =>
      apiFetch('/organizations/invites', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    createTaskTemplate: (taskData: {
      title: string;
      description: string;
      difficulty: string;
      timeEstimate: number;
      tags: string[];
      instructions: string;
      starterCode: string;
      solutionCode: string;
      tests: Array<{
        description: string;
        input: string;
        expected: string;
        isHidden: boolean;
      }>;
      status: string;
    }): Promise<any> => apiFetch('/interviewer/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    }),
    updateTask: (id: string, task: Partial<Task>): Promise<Task> => apiFetch(`/interviewer/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(task),
    }),
    
    // Submissions
    getSubmissions: (taskId: string): Promise<Submission[]> => apiFetch(`/interviewer/tasks/${taskId}/submissions`),
    createSubmission: (taskId: string, submission: Omit<Submission, 'id' | 'taskId'> & { ai_metadata?: any }): Promise<Submission> =>
      apiFetch(`/candidate/tasks/${taskId}/submissions`, {
        method: 'POST',
        body: JSON.stringify(submission),
      }),
    getSubmissionResults: (taskId: string, submissionId: string): Promise<any> => // TODO: Define SubmissionResult type
      apiFetch(`/candidate/tasks/${taskId}/submissions/${submissionId}`),
    runTaskTests: (taskId: string, code: string): Promise<any> =>
      apiFetch(`/candidate/tasks/${taskId}/test`, {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    
    // Support Tickets
    getSupportTickets: (): Promise<SupportTicket[]> => apiFetch('/support/tickets'),
    createSupportTicket: (ticket: Omit<SupportTicket, 'id'>): Promise<SupportTicket> =>
      apiFetch('/support/tickets', {
        method: 'POST',
        body: JSON.stringify(ticket),
      }),
    updateSupportTicket: (id: string, ticket: Partial<SupportTicket>): Promise<SupportTicket> =>
      apiFetch(`/support/tickets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(ticket),
      }),
    
    // Onboarding
    getOnboardingStatus: (): Promise<any> => apiFetch('/onboarding/status'), // TODO: Define OnboardingStatus type
    completeOnboarding: (): Promise<any> => apiFetch('/onboarding/complete', { // TODO: Define specific response type
      method: 'POST',
    }),
    
    // Organization
    createOrganization,
    getOrganization,
    updateOrganization,
    getOrganizationMembers,
    inviteOrganizationMember,
    resendInvitation,
    getOrganizationTasks,
    
    // Organization Settings
    getOrganizationSettings: (orgId: string): Promise<any> =>
      apiFetch(`/organizations/${orgId}/settings`),
    updateOrganizationSettings: (orgId: string, settings: { allow_ai: boolean }): Promise<any> =>
      apiFetch(`/organizations/${orgId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),
    
    // GitHub Integration
    integrateGitHubRepo: (orgId: string, repoUrl: string): Promise<any> => 
      apiFetch(`/organizations/${orgId}/integrations/github`, {
        method: 'POST',
        body: JSON.stringify({ repositoryUrl: repoUrl }),
      }),
    getGitHubDocs: (orgId: string): Promise<any> =>
      apiFetch(`/organizations/${orgId}/integrations/github/docs`),
    refreshGitHubDocs: (orgId: string): Promise<any> =>
      apiFetch(`/organizations/${orgId}/integrations/github/docs/refresh`, {
        method: 'POST',
      }),
    
    // Admin endpoints
    getAdminMetrics: (): Promise<any> => apiFetch('/admin/metrics'),
    getAuditLogs: (filters?: { userId?: string; action?: string; startDate?: string; endDate?: string }): Promise<any> => {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
      }
      return apiFetch(`/admin/audit-logs${params.toString() ? `?${params.toString()}` : ''}`);
    },
    getSystemStats: (): Promise<any> => apiFetch('/admin/stats'),
    
    // Enhanced documentation with GitHub integration
    getDocumentationCategories: (): Promise<any> => apiFetch('/documentation/categories'),
    getDocumentationList: (categoryId?: string, search?: string, page: number = 1, limit: number = 20): Promise<any> => {
      const params = new URLSearchParams();
      if (categoryId) params.append('categoryId', categoryId);
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      return apiFetch(`/documentation/documents?${params.toString()}`);
    },
    getDocumentContent: (documentId: string): Promise<any> => apiFetch(`/documentation/documents/${documentId}`),
    
    // Analytics endpoints
    getUserAnalytics: (userId: string, days: number = 30): Promise<any> =>
      apiFetch(`/analytics/user/${userId}?days=${days}`),
    getTaskAnalytics: (taskId: string): Promise<any> =>
      apiFetch(`/analytics/task/${taskId}`),
    getOrgAnalytics: (orgId: string, days: number = 30): Promise<any> =>
      apiFetch(`/analytics/org/${orgId}?days=${days}`),
    
    // Job Simulations
    createSimulation: (data: {
      title: string;
      description?: string;
      assigned_to: string;
      task_ids: string[];
      start_date: string;
      end_date: string;
      sprint_goals?: string[];
      difficulty_level?: string;
      estimated_hours?: number;
    }): Promise<any> =>
      apiFetch('/simulations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getUserSimulations: (userId: string, status?: string, page: number = 1, limit: number = 20): Promise<any> => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      return apiFetch(`/simulations/${userId}?${params.toString()}`);
    },
    updateSimulation: (simulationId: string, data: { status?: string; current_task_index?: number }): Promise<any> =>
      apiFetch(`/simulations/${simulationId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    
    // Gamification endpoints
    getGamificationProfile: async (): Promise<any> => {
      try {
        return await apiFetch('/gamification/profile/me');
      } catch (error: any) {
        // If profile doesn't exist (404), try to create it
        if (error.message?.includes('404') || error.status === 404) {
          try {
            console.log('Gamification profile not found, attempting to create...');
            // Try to create a new profile
            const createResponse = await apiFetch('/gamification/profile', {
              method: 'POST',
              body: JSON.stringify({})
            });
            console.log('Gamification profile created successfully');
            return createResponse;
          } catch (createError: any) {
            console.warn('Failed to create gamification profile:', createError);
            // Return a default empty profile structure if creation fails
            return {
              user_id: '',
              username: '',
              total_points: 0,
              current_level: 1,
              xp_progress: 0,
              xp_for_next_level: 100,
              badges: [],
              tasks_completed: 0,
              perfect_submissions: 0,
              streak_days: 0,
              points_by_category: {}
            };
          }
        }
        throw error;
      }
    },
    getLeaderboard: (leaderboardType: string = 'global', period: string = 'all-time', limit: number = 5): Promise<any> =>
      apiFetch(`/gamification/leaderboard?leaderboard_type=${leaderboardType}&period=${period}&limit=${limit}`),
    takePublicChallenge: (data: { difficulty?: string; tags?: string[] }): Promise<any> =>
      apiFetch('/gamification/public-challenge', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getDailyChallenge: (): Promise<any> =>
      apiFetch('/gamification/daily-challenge'),
    submitPublicChallenge: (taskId: string, submission: any): Promise<any> =>
      apiFetch(`/gamification/submit-public-challenge/${taskId}`, {
        method: 'POST',
        body: JSON.stringify(submission),
      }),
    getBadges: (): Promise<any> =>
      apiFetch('/gamification/badges'),
    
    // AI Customer endpoints
    getAICustomerResponse: (data: { 
      task_id: string;
      message: string; 
      task_title: string; 
      task_description: string; 
      conversation_history: Array<{ role: string; content: string }> 
    }): Promise<any> =>
      apiFetch('/customers/chat', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getChatHistory: (taskId: string): Promise<any> =>
      apiFetch(`/customers/chat/history/${taskId}`),
    debugChatMessages: (taskId: string): Promise<any> =>
      apiFetch(`/customers/chat/debug/${taskId}`),
    
    // Health check
    healthCheck: (): Promise<any> => apiFetch('/health'), // TODO: Define HealthStatus type
    
    // Invitation endpoints
    getInvitationDetails: (token: string): Promise<any> =>
      apiFetch(`/invitations/accept/${token}`),
    acceptInvitation: (token: string): Promise<any> =>
      apiFetch(`/invitations/accept/${token}`, {
        method: 'POST',
      }),
    
    // Documentation endpoints (not in OpenAPI spec, may need to be updated)
    getDocumentation: (companyId: string): Promise<any> => // TODO: Define DocumentationData type
      apiFetch(`/documentation/${companyId}`),
    getDocumentationFile: (companyId: string, fileId: string): Promise<any> => // TODO: Define DocumentationFileContent type
      apiFetch(`/documentation/${companyId}/${fileId}`),
    createTemplate,
    getTemplates,
    getTemplate,
    updateTemplate,
    deleteTemplate,
    
    // Task evaluation
    evaluateTask: async (taskId: string) => {
      const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/evaluate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getAuthHeaders() as Record<string, string>)
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to evaluate task: ${response.statusText}`);
      }

      return response.json();
    },
    
    // Evaluation endpoints
    getInterviewerSubmissions: (): Promise<any> =>
      apiFetch('/evaluation/submissions'),
    startEvaluation: (submissionId: string): Promise<any> =>
      apiFetch(`/evaluation/submissions/${submissionId}/start`, {
        method: 'POST',
      }),
    
    // Notifications
    getNotifications: (): Promise<any> =>
      apiFetch('/notifications'),
    markNotificationRead: (notificationId: string): Promise<any> =>
      apiFetch(`/notifications/${notificationId}/read`, {
        method: 'POST',
      }),
    getNotificationSettings: (): Promise<any> =>
      apiFetch('/notifications/settings'),
    updateNotificationSettings: (settings: {
      email_notifications?: boolean;
      sms_notifications?: boolean;
      notification_preferences?: {
        task_assigned?: boolean;
        submission_received?: boolean;
        evaluation_complete?: boolean;
        invitation_sent?: boolean;
      };
    }): Promise<any> =>
      apiFetch('/notifications/settings', {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),
  }), []);
}; 

// Cache for the access token
let cachedToken: string | null = null;
let tokenExpiryTime: number | null = null;

// Helper to get the access token for direct API calls
const getAccessToken = async (): Promise<string> => {
  // Check if we have a valid cached token
  if (cachedToken && tokenExpiryTime && tokenExpiryTime > Date.now()) {
    return cachedToken;
  }

  try {
    // Try multiple token sources
    const readTokenFromStorage = (storage: Storage, key: string) => {
      try {
        const session = storage.getItem(key);
        if (!session) return null;
        const data = JSON.parse(session);
        if (data?.token) {
          // Set expiry to 5 minutes before actual expiry to be safe
          const expiry = new Date(data.refreshExpiry || new Date(Date.now() + 3600000)).getTime() - 5 * 60 * 1000;
          cachedToken = data.token;
          tokenExpiryTime = expiry;
          return data.token;
        }
        return null;
      } catch {
        return null;
      }
    };
    
    // 1) Try sessionStorage
    let token = readTokenFromStorage(sessionStorage, 'descope.session');
    
    // 2) Try localStorage
    if (!token) {
      token = readTokenFromStorage(localStorage, 'descope.session');
    }
    
    // 3) Try window global (set by AuthContext)
    if (!token && (window as any).__DESCOPE_JWT__) {
      token = (window as any).__DESCOPE_JWT__;
      cachedToken = token;
      tokenExpiryTime = Date.now() + 3600000; // Assume 1hr validity
    }
    
    if (token) {
      return token;
    }
    
    // If we can't get the token, return empty string and let the API call fail appropriately
    console.warn('No authentication token available');
    return '';
  } catch (error) {
    console.error('Error getting access token:', error);
    return '';
  }
};

// Helper for direct API fetch (without using the hook)
const directApiFetch = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    const token = await getAccessToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${API_PREFIX}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Direct export functions for components that don't want to use the hook
// Tasks
export const getTasks = async (): Promise<{ tasks?: Task[]; data?: Task[]; error?: string }> => {
  try {
    const response = await directApiFetch('/candidate/tasks');
    
    // TODO: Backend should ideally return a consistent structure for this endpoint.
    // Current frontend code attempts to handle multiple possible response shapes.
    if (response?.tasks && Array.isArray(response.tasks)) {
      return { tasks: response.tasks as Task[] };
    } else if (Array.isArray(response)) {
      return { data: response as Task[] };
    } else if (response?.data && Array.isArray(response.data)) {
      return { data: response.data as Task[] };
    } else {
      // Fallback for any other structure, ensuring it matches one of the expected keys
      console.warn('getTasks directApiFetch: Unexpected response structure', response);
      return { data: [] }; // Default to empty data array
    }
  } catch (error: any) {
    console.error('getTasks API request failed:', error);
    // Return consistent error shape or empty data
    return { data: [], error: error?.message || 'Failed to fetch tasks' };
  }
};

export const getTask = async (id: string): Promise<Task> => directApiFetch(`/candidate/tasks/${id}`);

// Organization direct exports
export const createOrganization = async (data: CreateOrganizationData): Promise<Organization> => 
  directApiFetch('/organizations', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getOrganization = async (id: string): Promise<Organization> => 
  directApiFetch(`/organizations/${id}`);

// Submissions
export const createSubmission = async (taskId: string, submission: Omit<Submission, 'id' | 'taskId'>): Promise<Submission> =>
  directApiFetch(`/candidate/tasks/${taskId}/submissions`, {
    method: 'POST',
    body: JSON.stringify(submission),
  });

// Support
export const createSupportTicket = async (ticket: Omit<SupportTicket, 'id'>): Promise<SupportTicket> =>
  directApiFetch('/support/tickets', {
    method: 'POST',
    body: JSON.stringify(ticket),
  });

// Health
export const healthCheck = async (): Promise<any> => directApiFetch('/health'); // TODO: Define HealthStatus type

// Documentation endpoints (not in OpenAPI spec, may need to be updated)
// TODO: Verify contracts for these documentation endpoints
export const getDocumentationFile = async (companyId: string, fileId: string): Promise<any> => // TODO: Define DocumentationFileContent type
  directApiFetch(`/documentation/${companyId}/${fileId}`);

// Invitation endpoints
export const getInvitationDetails = async (token: string): Promise<any> =>
  directApiFetch(`/invitations/accept/${token}`);

export const acceptInvitation = async (token: string): Promise<any> =>
  directApiFetch(`/invitations/accept/${token}`, {
    method: 'POST',
  });

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Direct export functions for gamification
export const takePublicChallenge = async (data: { difficulty?: string; tags?: string[] }): Promise<any> => {
  try {
    return await directApiFetch('/gamification/public-challenge', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error('Failed to get public challenge:', error);
    throw error;
  }
};

export const getGamificationProfile = async (): Promise<any> => {
  try {
    return await directApiFetch('/gamification/profile/me');
  } catch (error: any) {
    // If profile doesn't exist (404), try to create it
    if (error.message?.includes('404') || error.status === 404) {
      try {
        console.log('Gamification profile not found, attempting to create...');
        const createResponse = await directApiFetch('/gamification/profile', {
          method: 'POST',
          body: JSON.stringify({})
        });
        console.log('Gamification profile created successfully');
        return createResponse;
      } catch (createError: any) {
        console.warn('Failed to create gamification profile:', createError);
        // Return a default empty profile structure if creation fails
        return {
          user_id: '',
          username: '',
          total_points: 0,
          current_level: 1,
          xp_progress: 0,
          xp_for_next_level: 100,
          badges: [],
          tasks_completed: 0,
          perfect_submissions: 0,
          streak_days: 0,
          points_by_category: {}
        };
      }
    }
    console.error('Failed to get gamification profile:', error);
    throw error;
  }
};

// Export updateUserProfile directly
export const updateUserProfile = async (data: { 
  name?: string;
  bio?: string;
  location?: string;
  portfolio?: string;
  linkedin?: string;
  github?: string;
  phone?: string;
  skills?: string;
  timezone?: string;
}): Promise<any> => {
  try {
    return await directApiFetch('/users/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error('Failed to update user profile:', error);
    throw error;
  }
};